import type {
  CompiledTemplateCampaign,
  TemplatePortfolioDiagnosticCode,
  TemplatePortfolioDraft,
} from "@adpilot/shared";
import { IconAlertCircle, IconArrowRight, IconPlayerPause } from "@tabler/icons-react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import {
  editableSettings,
  previewTemplatePortfolioDraft,
  type TemplatePortfolioEditableSettings,
  templatePortfolioKeys,
  useExecuteTemplatePortfolioDraft,
  useUpdateTemplatePortfolioDraft,
} from "./api";
import { CampaignSheet } from "./preview/CampaignSheet";
import { CampaignTable } from "./preview/CampaignTable";
import { CategoryTargetCard } from "./preview/CategoryTargetCard";
import {
  type TemplatePortfolioDraftStepProps,
  TemplatePortfolioFooter,
} from "./TemplatePortfolioShell";

export interface StepPreviewProps extends TemplatePortfolioDraftStepProps {
  /** Called with the execution returned by POST .../execute; the shell moves to ④ 创建. */
  onExecutionStarted: (executionId: string) => void;
}

const TEMPLATE_DAILY_BUDGET = 20_000;
const BLOCKING: TemplatePortfolioDiagnosticCode[] = [
  "template_portfolio.portfolio.name_conflict",
  "template_portfolio.campaign.duplicate_name",
  "template_portfolio.campaign.no_targets",
  "template_portfolio.negatives.limit_exceeded",
];

/** ③ 结构预览 (docs/tech/12 §3 ③, Paper TP-09 / 09a / 09b). */
export function StepPreview({ shopId, draft, onBack, onExecutionStarted }: StepPreviewProps) {
  const { t } = useTranslation("templatePortfolio");
  const queryClient = useQueryClient();
  // Keep the last compile on screen while an edit recompiles under the new version.
  const preview = useQuery({
    queryKey: templatePortfolioKeys.preview(draft.id, draft.version),
    queryFn: () => previewTemplatePortfolioDraft(shopId, draft.id),
    retry: false,
    placeholderData: keepPreviousData,
  });
  const update = useUpdateTemplatePortfolioDraft(shopId, draft.id);
  const execute = useExecuteTemplatePortfolioDraft(shopId, draft.id);
  const [openKey, setOpenKey] = useState<string | null>(null);

  // Edits are serialized and always PATCH on top of the latest cached draft, so two
  // quick edits never race on the same expectedVersion.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const saveSettings = useCallback(
    (patch: (settings: TemplatePortfolioEditableSettings) => TemplatePortfolioEditableSettings) => {
      const run = queue.current.then(async () => {
        const latest =
          queryClient.getQueryData<TemplatePortfolioDraft>(templatePortfolioKeys.draft(draft.id)) ??
          draft;
        try {
          await update.mutateAsync({
            expectedVersion: latest.version,
            settings: patch(editableSettings(latest.settings)),
          });
          return true;
        } catch (error) {
          toast.error(await getApiErrorMessage(error));
          return false;
        }
      });
      queue.current = run;
      return run;
    },
    [draft, queryClient, update],
  );

  if (preview.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-[106px] w-full rounded-[14px]" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[90px] w-full rounded-[14px]" />
        <Skeleton className="h-96 w-full rounded-[14px]" />
      </div>
    );
  }
  if (preview.isError) {
    return (
      <>
        <ErrorState
          message={t("preview.loadError")}
          onRetry={() => void preview.refetch()}
          isRetrying={preview.isFetching}
        />
        <TemplatePortfolioFooter
          hint={
            <Button variant="outline" onClick={onBack}>
              {t("actions.back")}
            </Button>
          }
        >
          {null}
        </TemplatePortfolioFooter>
      </>
    );
  }

  const { portfolio, diagnostics } = preview.data;
  const has = (code: TemplatePortfolioDiagnosticCode) => diagnostics.some((d) => d.code === code);
  const warnings = new Map<string, string>();
  for (const diagnostic of diagnostics) {
    const label =
      diagnostic.code === "template_portfolio.campaign.duplicate_name"
        ? t("preview.table.duplicate")
        : diagnostic.code === "template_portfolio.campaign.name_truncated"
          ? t("preview.table.truncated")
          : diagnostic.code === "template_portfolio.campaign.no_targets"
            ? t("preview.table.noTargets")
            : null;
    if (!label) continue;
    for (const key of diagnostic.campaignKeys) if (!warnings.has(key)) warnings.set(key, label);
  }
  const negativeCount = draft.keywords.filter(
    (keyword) => keyword.category === "negative" && keyword.selected,
  ).length;
  const blocked = diagnostics.some((d) => BLOCKING.includes(d.code));
  const stale = preview.isPlaceholderData || preview.data.draftVersion !== draft.version;
  const count = portfolio.summary.campaignCount;
  const openCampaign = portfolio.campaigns.find((campaign) => campaign.key === openKey) ?? null;

  async function deleteCampaign(campaign: CompiledTemplateCampaign) {
    return saveSettings((settings) => ({
      ...settings,
      excludedCampaignKeys: [...new Set([...(settings.excludedCampaignKeys ?? []), campaign.key])],
    }));
  }

  async function startExecution() {
    try {
      const execution = await execute.mutateAsync(draft.version);
      onExecutionStarted(execution.id);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
      void queryClient.invalidateQueries({ queryKey: templatePortfolioKeys.draft(draft.id) });
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {has("template_portfolio.portfolio.name_conflict") ? (
          <Notice tone="destructive">{t("preview.diagnostics.nameConflict")}</Notice>
        ) : null}
        {has("template_portfolio.campaign.duplicate_name") ? (
          <Notice tone="destructive">{t("preview.diagnostics.duplicateName")}</Notice>
        ) : null}
        {has("template_portfolio.campaign.no_targets") ? (
          <Notice tone="destructive">{t("preview.diagnostics.noTargets")}</Notice>
        ) : null}
        {has("template_portfolio.negatives.limit_exceeded") ? (
          <Notice tone="destructive">{t("preview.diagnostics.tooManyNegatives")}</Notice>
        ) : null}
        {has("template_portfolio.overrides.stale") ? (
          <Notice tone="info">{t("preview.diagnostics.stale")}</Notice>
        ) : null}

        <section className="flex rounded-[14px] border border-line bg-surface">
          <SummaryCell className="grow-[1.3]" label={t("preview.summary.portfolio")}>
            <span className="text-lg font-semibold text-ink">{portfolio.portfolioName}</span>
            {draft.settings.product ? (
              <span className="text-xs text-muted-foreground">
                {t("preview.summary.sku", { sku: draft.settings.product.sku })}
              </span>
            ) : null}
          </SummaryCell>
          <SummaryCell label={t("preview.summary.campaigns")}>
            <SummaryNumber value={count} />
          </SummaryCell>
          <SummaryCell
            label={t("preview.summary.keywordTargets")}
            hint={t("preview.summary.keywordTargetsHint")}
          >
            <SummaryNumber value={portfolio.summary.keywordTargetCount} />
          </SummaryCell>
          <SummaryCell
            last
            label={t("preview.summary.negatives")}
            hint={t("preview.summary.negativesHint")}
          >
            <SummaryNumber value={negativeCount} />
          </SummaryCell>
        </section>

        <div className="flex items-center gap-2 rounded-[10px] border border-badge-warning-border bg-badge-warning-background px-3.5 py-2.5 text-xs text-warning-foreground">
          <IconPlayerPause className="size-4 shrink-0" stroke={1.8} aria-hidden />
          {t("preview.pausedNotice", {
            budget: `$${TEMPLATE_DAILY_BUDGET.toLocaleString("en-US")}`,
          })}
        </div>

        <CategoryTargetCard
          shopId={shopId}
          draftId={draft.id}
          value={draft.settings.categoryTarget}
          onSave={(categoryTarget) => saveSettings((settings) => ({ ...settings, categoryTarget }))}
        />

        <CampaignTable
          campaigns={portfolio.campaigns}
          warnings={warnings}
          onOpen={(campaign) => setOpenKey(campaign.key)}
          onDelete={deleteCampaign}
        />
      </div>

      <CampaignSheet
        campaign={openCampaign}
        override={openKey ? draft.settings.campaignOverrides?.[openKey] : undefined}
        onClose={() => setOpenKey(null)}
        onSave={(override) =>
          saveSettings((settings) => ({
            ...settings,
            campaignOverrides: {
              ...settings.campaignOverrides,
              [openKey as string]: override,
            },
          }))
        }
      />

      <TemplatePortfolioFooter
        hint={
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onBack}>
              {t("actions.back")}
            </Button>
            {count === 0 ? (
              <span className="text-xs text-muted-foreground">{t("preview.noCampaigns")}</span>
            ) : null}
          </div>
        }
      >
        <Button
          loading={execute.isPending}
          disabled={blocked || stale || count === 0 || update.isPending}
          onClick={() => void startExecution()}
        >
          {t("preview.create", { count })}
          <IconArrowRight className="size-3.5" stroke={1.8} aria-hidden />
        </Button>
      </TemplatePortfolioFooter>
    </>
  );
}

function SummaryCell({
  label,
  hint,
  last,
  className,
  children,
}: {
  label: string;
  hint?: string;
  last?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 basis-0 flex-col gap-1 px-5 py-4",
        !last && "border-r border-line",
        className,
      )}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

function SummaryNumber({ value }: { value: number }) {
  return (
    <span className="text-2xl font-bold tracking-[-0.01em] text-ink tabular-nums">
      {value.toLocaleString("en-US")}
    </span>
  );
}

function Notice({ tone, children }: { tone: "destructive" | "info"; children: React.ReactNode }) {
  return (
    <div
      role={tone === "destructive" ? "alert" : "status"}
      className={
        tone === "destructive"
          ? "flex items-center gap-2 rounded-[10px] border border-badge-destructive-border bg-badge-destructive-background px-3.5 py-2.5 text-xs text-badge-destructive-foreground"
          : "flex items-center gap-2 rounded-[10px] border border-badge-information-border bg-badge-information-background px-3.5 py-2.5 text-xs text-badge-information-foreground"
      }
    >
      <IconAlertCircle className="size-4 shrink-0" stroke={1.8} aria-hidden />
      {children}
    </div>
  );
}
